/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import type { LinkProps } from '@hover/blueprint';
import {
  Box,
  Form,
  Icon,
  IconButton,
  Link,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  TextInput,
} from '@hover/blueprint';
import { useMediaQuery } from '@hover/blueprint/chakra';
import { hClose, iMenu, iSearch } from '@hover/icons';
import React, { useState } from 'react';

import type { TrackingProps } from 'src/modules/newConstructionUpload/types/newConstructionUploadTypes';
import { generateFilterUrl } from 'src/modules/properties/utils/filtersUtils';
import { useFeatureAccesses } from 'src/modules/propertyDetails/hooks/useFeatureAccesses';
import { useHasAccessForOrg } from 'src/modules/propertyDetails/hooks/useHasAccessForOrg';
import { useIsHoverAdmin } from 'src/modules/propertyDetails/hooks/useIsHoverAdmin';
import { useIsMobileSize } from 'src/modules/renderings/hooks';
import { MobileNavSheet } from 'src/shared/components/NavBar/MobileNavSheet';
import {
  LOGO_MIN_WIDTH,
  StyledNavBar,
} from 'src/shared/components/NavBar/StyledNavBar';
import { UserIcon } from 'src/shared/components/NavBar/UserIcon';
import type {
  JobSearchFilters,
  JobTypeFilters,
} from 'src/shared/graphql/generated';
import {
  AclTemplateOptions,
  FeatureAccessIdentifier,
  useProfileQuery,
} from 'src/shared/graphql/generated';
import { getQueryParamsWithoutSearch } from 'src/shared/helpers/url';
import {
  TranslationNamespace,
  useCurrentPage,
  useLocation,
  useTranslate,
} from 'src/shared/hooks';
import { useBrand } from 'src/shared/providers/Brand';
import { External, Route } from 'src/shared/route';
import { textInput } from 'src/shared/telemetry';

interface NavLinkProps extends LinkProps {
  active?: boolean;
}

const NavMenuItem: React.FC<{ path: string; display: string }> = ({
  path,
  display,
}) => (
  <MenuItem as="a" href={path} paddingY={300} target="self" width="unset">
    {display}
  </MenuItem>
);

const NavLink = ({ ...props }: NavLinkProps) => (
  <Link
    _hover={{ textDecoration: 'underline', textDecorationColor: 'neutral.500' }}
    color="neutral.900"
    fontSize={400}
    justifyContent="center"
    marginRight={`${LOGO_MIN_WIDTH}px`}
    textDecoration={props.active ? 'underline' : 'none'}
    textDecorationColor="neutral.900"
    textUnderlineOffset="1rem"
    whiteSpace="nowrap"
    {...props}
  />
);

export enum NavigationLinks {
  Projects = 'Projects',
  ManageTeam = 'ManageTeam',
  ProjectsList = 'ProjectsList', // sigh
}

const NavigationBar = ({ onLogout }: { onLogout: () => void }) => {
  const tCommon = useTranslate(TranslationNamespace.COMMON);
  const { data } = useProfileQuery();
  const {
    isEstimationEnabled,
    isMaterialListEnabled,
    isInspectionChecklistEnabled,
  } = useFeatureAccesses();
  const [isTeamAdmin] = useHasAccessForOrg([AclTemplateOptions.Admin]);
  const [isJobManager] = useHasAccessForOrg([AclTemplateOptions.JobManager]);

  const { searchParams } = useLocation();
  const searchParam = searchParams.get('search');
  const filterParams = searchParams.getAll('filters');
  const jobTypeFilter = searchParams.get('jobTypes');
  const exampleParam = searchParams.get('example');
  const deliverableIdFilter = searchParams.get('deliverableIds');
  const [isMobileOrTablet] = useMediaQuery('(max-width: 834px)');

  const currentPage = useCurrentPage();
  const isMobileSize = useIsMobileSize();
  const [searchTerm, setSearchTerm] = useState<string>(searchParam ?? '');
  const [isMobileNavOpen, setIsMobileNavOpen] = useState<boolean>(false);
  const isHoverAdmin = useIsHoverAdmin();

  const canManageEhiProjects = isTeamAdmin || isJobManager;
  const isManageTeams = currentPage === Route.MANAGE_TEAMS.page;
  const isEhiPage = window.location.href.includes('/ehi/#/');
  const isManageTeamsInvite = window.location.href.includes(
    'manage-teams/invite',
  );
  const shouldShowSearch =
    currentPage !== Route.PROPERTY_DETAILS.page &&
    !isEhiPage &&
    !isManageTeamsInvite;

  const user = data?.profile?.user;
  const org = user?.orgs[0];

  const featureAccesses = org?.featureAccesses;
  const hasEstimationAccess =
    featureAccesses &&
    featureAccesses.some(
      v => v.identifier === FeatureAccessIdentifier.SalesEstimation,
    );

  const { loadingBrand } = useBrand();

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const currentActiveNavLink = () => {
    if (window.location.pathname === Route.PROPERTIES.path)
      return NavigationLinks.Projects;
    return null;
  };

  const getFiltersUrl = () => {
    const currentFilterParams = {
      ...(filterParams?.length
        ? { filters: filterParams as JobSearchFilters[] }
        : {}),
      ...(jobTypeFilter ? { jobTypes: [jobTypeFilter as JobTypeFilters] } : {}),
      ...(deliverableIdFilter ? { deliverableIds: [deliverableIdFilter] } : {}),
      ...(exampleParam === 'true' ? { example: true } : { example: false }),
    };

    return generateFilterUrl(currentFilterParams);
  };

  const trackingProps = (): TrackingProps => ({
    user_email: user?.email ?? '',
    org_id: user?.orgs[0].id,
    page_or_screen_name: 'Projects',
    partner_name: (user?.orgs[0].partner?.name as string) ?? '',
    user_id: user?.id,
    platform: isMobileOrTablet ? 'mobile' : 'web',
  });

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = event => {
    event.preventDefault();

    const searchValue = (event.currentTarget.elements[0] as HTMLInputElement)
      .value;

    if (searchValue.length < 3) return;

    textInput({
      input_value: searchTerm,
      input_label: 'search',
      ...trackingProps(),
    });

    let pathWithSearchParams;

    if (isManageTeams) {
      const existingParams = getQueryParamsWithoutSearch(location.search);
      pathWithSearchParams = `${
        Route.MANAGE_TEAMS.path
      }?search=${encodeURIComponent(searchValue)}${
        existingParams ? `&${existingParams}` : ''
      }`;
    } else {
      const filterUrlParams = getFiltersUrl();
      pathWithSearchParams = `${Route.PROPERTIES.path}?search=${searchValue}${
        filterUrlParams ? `&${filterUrlParams}` : ''
      }`;
    }
    window.location.href = pathWithSearchParams;
  };

  const clearSearch = () => {
    setSearchTerm('');
    let pathWithoutSearchParams;
    if (isManageTeams) {
      pathWithoutSearchParams = `${Route.MANAGE_TEAMS.path}?${
        getQueryParamsWithoutSearch(location.search) ?? ''
      }`;
    } else {
      const filterUrlParams = getFiltersUrl();
      pathWithoutSearchParams = `${Route.PROPERTIES.path}${
        filterUrlParams ? `?${filterUrlParams}` : ''
      }`;
    }

    window.location.href = pathWithoutSearchParams;
  };

  if (loadingBrand) return null;

  const showWorkflows =
    isMaterialListEnabled ||
    ((isEstimationEnabled || isInspectionChecklistEnabled) &&
      canManageEhiProjects);

  return isMobileSize ? (
    <Box
      as="header"
      backgroundColor="neutral.0"
      padding={200}
      position="sticky"
      top={0}
      zIndex={1}
    >
      <IconButton
        color="neutral"
        fill="minimal"
        icon={iMenu}
        label="Nav"
        onClick={() => setIsMobileNavOpen(true)}
        size="large"
      />
      {isManageTeams && (
        <Form onSubmit={handleSubmit} width="100%">
          <TextInput
            {...(searchTerm && searchTerm.length
              ? {
                  elementAfter: (
                    <IconButton
                      color="neutral"
                      fill="minimal"
                      icon={hClose}
                      label="Clear search"
                      onClick={clearSearch}
                      shape="square"
                    />
                  ),
                }
              : {})}
            alignItems="center"
            backgroundColor="neutral.100"
            border="none"
            elementBefore={<Icon fill="minimal" icon={iSearch} />}
            onChange={onChange}
            placeholder={
              isManageTeams
                ? 'Search user by name or email'
                : 'Search for project by address, id, or name'
            }
            value={searchTerm}
            width="100%"
          />
        </Form>
      )}
      <MobileNavSheet isOpen={isMobileNavOpen} setIsOpen={setIsMobileNavOpen} />
    </Box>
  ) : (
    <StyledNavBar>
      <Box flex={1}>
        <NavLink
          active={currentActiveNavLink() === NavigationLinks.Projects}
          href={Route.PROPERTIES.path}
        >
          {tCommon('navigation.Jobs')}
        </NavLink>
        {isTeamAdmin && (
          <NavLink href={Route.MANAGE_TEAMS.path}>
            {tCommon('navigation.manageTeam')}
          </NavLink>
        )}
        {isEstimationEnabled && hasEstimationAccess && (
          <NavLink href={External.PROJECT_LIST.path}>
            {tCommon('navigation.Estimates')}
          </NavLink>
        )}
        {isHoverAdmin && (
          <NavLink href={External.ADMIN.path}>
            {tCommon('navigation.admin')}
          </NavLink>
        )}
      </Box>
      {shouldShowSearch && (
        <Box flex={1}>
          <Form onSubmit={handleSubmit} width="100%">
            <TextInput
              {...(searchTerm && searchTerm.length
                ? {
                    elementAfter: (
                      <IconButton
                        color="neutral"
                        fill="minimal"
                        icon={hClose}
                        label="Clear search"
                        onClick={clearSearch}
                        shape="square"
                      />
                    ),
                  }
                : {})}
              alignItems="center"
              backgroundColor="neutral.100"
              border="none"
              elementBefore={<Icon fill="minimal" icon={iSearch} />}
              onChange={onChange}
              placeholder={
                isManageTeams
                  ? 'Search user by name or email'
                  : 'Search for project by address, id, or name'
              }
              value={searchTerm}
              width="100%"
            />
          </Form>
        </Box>
      )}
      <Box flex={1} justifyContent="flex-end">
        <Menu
          trigger={
            <MenuButton
              color="neutral"
              fill="minimal"
              fontWeight={300}
              height="52px"
            >
              <UserIcon name={user?.name} />
            </MenuButton>
          }
        >
          {showWorkflows && (
            <NavMenuItem
              display={tCommon('navigation.manageWorkflows')}
              path={External.MANAGE_WORKFLOWS.path}
            />
          )}
          {isTeamAdmin && (
            <NavMenuItem
              display={tCommon('navigation.planAndBilling')}
              path={External.PLAN.path}
            />
          )}
          <NavMenuItem
            display={tCommon('navigation.settings')}
            path={External.SETTINGS.path}
          />
          <MenuDivider />
          <MenuItem onClick={onLogout} paddingY={300}>
            {tCommon('logOut')}
          </MenuItem>
        </Menu>
      </Box>
    </StyledNavBar>
  );
};

export { NavigationBar };
